import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, X, Check, XCircle, Bot, User } from 'lucide-react';
import apiClient from '../api/client';

const HRChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = { role: 'user', content: inputMessage };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const response = await apiClient.post('/api/chatbot/message', {
        message: inputMessage,
        session_id: 'main'
      });

      const botResponse = { role: 'assistant', content: response.data.message, data: response.data };
      setMessages(prev => [...prev, botResponse]);

      // Check if action requires confirmation
      if (response.data.requires_confirmation && response.data.action !== 'chat') {
        setPendingAction(response.data);
      }
    } catch (error) {
      const errorMsg = { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAction = async (confirmed) => {
    if (!pendingAction) return;

    setLoading(true);

    try {
      const response = await apiClient.post('/api/chatbot/execute', {
        action: pendingAction.action,
        parameters: pendingAction.parameters,
        confirmed
      });

      const resultMsg = {
        role: 'assistant',
        content: confirmed ? response.data.message : 'Action cancelled.'
      };
      setMessages(prev => [...prev, resultMsg]);
    } catch (error) {
      const errorMsg = { role: 'assistant', content: 'Error executing action.' };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setPendingAction(null);
      setLoading(false);
    }
  };

  const formatActionParams = (params) => {
    return Object.entries(params).map(([key, value]) => (
      <div key={key} className='text-xs'>
        <span className='font-semibold'>{key}:</span> {JSON.stringify(value)}
      </div>
    ));
  };

  return (
    <>
      {/* Chatbot Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className='fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-br from-[#00B8B8] to-[#0EA5E9] text-white rounded-full shadow-[0_0_20px_rgba(0,184,184,0.4)] hover:shadow-[0_0_30px_rgba(0,184,184,0.6)] transition-all z-50 flex items-center justify-center'
          data-testid='open-chatbot'
        >
          <MessageCircle size={28} />
        </button>
      )}

      {/* Chatbot Window */}
      {isOpen && (
        <div className='fixed bottom-6 right-6 w-96 h-[600px] bg-[hsl(220_6%_8%)] rounded-xl shadow-[0_12px_40px_rgba(0,0,0,0.50)] flex flex-col z-50 border border-[hsl(215_10%_22%)]'>
          {/* Header */}
          <div className='bg-gradient-to-r from-[#00B8B8] to-[#0EA5E9] text-white p-4 rounded-t-xl flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <Bot size={24} />
              <div>
                <h3 className='font-semibold'>HR Assistant</h3>
                <p className='text-xs opacity-90'>Danwei HR</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className='hover:bg-white/20 p-1 rounded transition-colors duration-150'>
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className='flex-1 overflow-y-auto p-4 space-y-4 bg-[hsl(220_6%_6%)]'>
            {messages.length === 0 && (
              <div className='text-center text-[hsl(215_10%_70%)] py-8'>
                <Bot size={48} className='mx-auto mb-4 opacity-50' />
                <p className='text-sm'>Hi! I'm your HR assistant.</p>
                <p className='text-xs mt-2 text-[hsl(215_10%_50%)]'>Try: "Deduct 5000 from employee 1001" or "Email payslip to 3591"</p>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-lg p-3 ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-r from-[#00B8B8] to-[#0EA5E9] text-white'
                    : 'bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] text-[hsl(210_15%_92%)]'
                }`}>
                  <div className='flex items-start gap-2'>
                    {msg.role === 'assistant' && <Bot size={16} className='mt-1 flex-shrink-0 text-[#00B8B8]' />}
                    <div className='flex-1'>
                      <p className='text-sm'>{msg.content}</p>
                      {msg.data?.parameters && (
                        <div className='mt-2 p-2 bg-[hsl(215_8%_20%)] rounded text-[hsl(210_15%_92%)]'>
                          {formatActionParams(msg.data.parameters)}
                        </div>
                      )}
                    </div>
                    {msg.role === 'user' && <User size={16} className='mt-1 flex-shrink-0' />}
                  </div>
                </div>
              </div>
            ))}

            {/* Pending Action Confirmation */}
            {pendingAction && (
              <div className='bg-[rgba(245,158,11,0.1)] border border-[rgba(245,158,11,0.3)] rounded-lg p-3'>
                <p className='text-sm font-semibold text-[#F59E0B] mb-2'>Confirm Action:</p>
                <p className='text-xs text-[hsl(215_10%_70%)] mb-3'>{pendingAction.action.replace('_', ' ').toUpperCase()}</p>
                <div className='flex gap-2'>
                  <button
                    onClick={() => handleConfirmAction(true)}
                    className='flex-1 flex items-center justify-center gap-1 bg-[#10B981] text-white px-3 py-2 rounded text-sm hover:bg-[#059669] transition-colors duration-150'
                  >
                    <Check size={16} />
                    Confirm
                  </button>
                  <button
                    onClick={() => handleConfirmAction(false)}
                    className='flex-1 flex items-center justify-center gap-1 bg-[#EF4444] text-white px-3 py-2 rounded text-sm hover:bg-[#DC2626] transition-colors duration-150'
                  >
                    <XCircle size={16} />
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {loading && (
              <div className='flex justify-start'>
                <div className='bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg p-3'>
                  <div className='flex items-center gap-2'>
                    <div className='loading-spinner w-4 h-4'></div>
                    <span className='text-sm text-[hsl(215_10%_70%)]'>Thinking...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className='p-4 border-t border-[hsl(215_10%_22%)] bg-[hsl(220_6%_8%)] rounded-b-xl'>
            <div className='flex gap-2'>
              <input
                type='text'
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder='Type your command...'
                className='flex-1 px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] placeholder-[hsl(215_10%_50%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20 focus:outline-none text-sm'
                disabled={loading}
              />
              <button
                onClick={handleSendMessage}
                disabled={loading || !inputMessage.trim()}
                className='bg-[#00B8B8] text-white p-2 rounded-lg hover:bg-[#0ED2D2] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150'
              >
                <Send size={20} />
              </button>
            </div>
            <p className='text-xs text-[hsl(215_10%_70%)] mt-2'>Ask me to adjust salaries, add employees, email payslips, etc.</p>
          </div>
        </div>
      )}
    </>
  );
};

export default HRChatbot;
